import { themeClassNames, type Mode, type PaletteId } from "@/lib/theme";
import { cn } from "@/lib/utils";

/** Two series for the preview's sparkline, in a 100×28 box. */
const SPARKS = ["0,20 20,14 40,17 60,8 80,11 100,4", "0,24 20,22 40,24 60,18 80,20 100,15"];

/**
 * A miniature of the app — sidebar, page title, a card with a sparkline and a
 * button — drawn in `mode` and `palette` by wearing their classes, so every
 * colour is the palette's real token rather than a copied hex. A palette edit
 * in `index.css` shows up here with nothing to keep in step.
 *
 * Decorative: hidden from assistive tech, the tile around it names the look.
 */
export function ThemePreview({ mode, palette }: { mode: Mode; palette: PaletteId }) {
  return (
    <div
      aria-hidden
      className={cn(
        themeClassNames(mode, palette),
        "pointer-events-none flex h-28 select-none bg-background-deep font-sans",
      )}
    >
      <div className="flex w-1/4 flex-col gap-1.5 border-r border-border p-2">
        <div className="h-2 w-3/4 rounded-full bg-primary" />
        <div className="mt-1.5 h-2 w-full rounded-sm bg-nav-active-bg" />
        <div className="h-1.5 w-4/5 rounded-full bg-text-dim/40" />
        <div className="h-1.5 w-3/5 rounded-full bg-text-dim/40" />
        <div className="h-1.5 w-2/3 rounded-full bg-text-dim/40" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-2">
        <div className="h-2 w-2/5 rounded-full bg-foreground/80" />
        <div className="flex flex-1 flex-col gap-1.5 rounded-md border border-border bg-card p-1.5 shadow-card">
          <div className="h-1.5 w-1/2 rounded-full bg-muted-foreground/50" />
          <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="h-5 w-full">
            {SPARKS.map((points, i) => (
              <polyline
                key={points}
                points={points}
                fill="none"
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ stroke: `var(--chart-${i + 1})`, strokeWidth: "1.5px" }}
              />
            ))}
          </svg>
          <div className="mt-auto flex gap-1">
            <div className="h-2.5 w-7 rounded-sm bg-primary-fill" />
            <div className="h-2.5 w-5 rounded-sm border border-border" />
          </div>
        </div>
      </div>
    </div>
  );
}
